import { useState, useCallback } from 'react';
import pick from 'lodash/pick';

import { CustomReportParamTypes } from '../../shared/customReports/constants';
import { getParamNamesForQuery } from '../../shared/customReports/helpers';
import { BatchReportParameters, isCustomReport } from '../../shared/types/batchReports';
import { isReportSet, isSingleReport, CustomReportParamDefinition, ParamValues } from '../../shared/types/customReports';

type VerifyParamOptionsQueryParams = {
  query: string,
  params?: Record<string, any>,
  name?: string,
  customReportType?: string,
  queries?: any[],
  onSuccess?: (response: any) => void,
  onFailure?: () => void,
};

type ValidatorInfo = {
  param: CustomReportParamDefinition,
  reportParamValues: ParamValues,
  reportSetId: string,
};

type UseBatchReportValidationParams = {
  batchReportParameters: BatchReportParameters,
  verifyParamOptionsQuery: (params: VerifyParamOptionsQueryParams) => void,
};

export const useBatchReportValidation = ({
  batchReportParameters,
  verifyParamOptionsQuery,
}: UseBatchReportValidationParams) => {
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [validationMessage, setValidationMessage] = useState('');
  const [pendingExecution, setPendingExecution] = useState<(() => void) | null>(null);

  const getValidatorParams = useCallback(() => {
    if (!batchReportParameters) {
      return [];
    }

    const allValidators: ValidatorInfo[] = [];
    const seenValidators = new Set<string>();

    Object.keys(batchReportParameters).forEach(itemName => {
      const reportParams = batchReportParameters[itemName];

      if (!reportParams.isIncludedInExport) {
        return;
      }

      const reportDefinition = reportParams.reportDefinition;

      if (!isCustomReport(reportDefinition)) {
        return;
      }

      if (isReportSet(reportDefinition)) {
        reportDefinition.reports.forEach(report => {
          if (report.params) {
            Object.values(report.params).forEach(param => {
              if (param.type === CustomReportParamTypes.validator) {
                const validatorKey = `${reportDefinition.reportSetId}_${report.reportSetId}_${param.name}_${param.query}`;
                if (!seenValidators.has(validatorKey)) {
                  seenValidators.add(validatorKey);

                  const validatorParamNames = getParamNamesForQuery(param.query);
                  const mappedValues: Record<string, any> = {};

                  validatorParamNames.forEach((paramName: string) => {
                    Object.entries(report.params).forEach(([reportParamName, paramDef]) => {
                      if (reportParamName === paramName) {
                        const paramId = `${report.reportSetId}${paramDef.type}_${paramName}`;
                        if (reportParams.reportParamValues[paramId]) {
                          mappedValues[paramName] = reportParams.reportParamValues[paramId];
                        }
                      }
                    });
                  });

                  allValidators.push({
                    param,
                    reportParamValues: mappedValues,
                    reportSetId: reportDefinition.reportSetId,
                  });
                }
              }
            });
          }
        });
      } else if (isSingleReport(reportDefinition)) {
        if (reportDefinition.params) {
          Object.values(reportDefinition.params).forEach(param => {
            if (param.type === CustomReportParamTypes.validator) {
              const validatorKey = `${reportDefinition.reportSetId}_${param.name}_${param.query}`;
              if (!seenValidators.has(validatorKey)) {
                seenValidators.add(validatorKey);
                allValidators.push({
                  param,
                  reportParamValues: reportParams.reportParamValues,
                  reportSetId: reportDefinition.reportSetId,
                });
              }
            }
          });
        }
      }
    });

    return allValidators;
  }, [batchReportParameters]);

  const runValidators = useCallback((onSuccess: () => void) => {
    const validatorParams = getValidatorParams();

    if (validatorParams.length === 0) {
      onSuccess();
      return;
    }

    let validatorsToCheck = validatorParams.length;
    let hasValidationError = false;
    let validationErrorMessage = '';

    const onValidationSuccess = (response: any) => {
      validatorsToCheck--;

      if (response && response.length > 0) {
        const validationResult = response[0].validationResult
          || response[0].validation_result
          || response[0].VALIDATION_RESULT;

        if (validationResult && validationResult !== 'OK') {
          hasValidationError = true;
          validationErrorMessage = validationResult;
        }
      }

      if (validatorsToCheck === 0) {
        if (hasValidationError) {
          setShowValidationModal(true);
          setValidationMessage(validationErrorMessage);
          setPendingExecution(() => onSuccess);
        } else {
          onSuccess();
        }
      }
    };

    const onValidationFailure = () => {
      validatorsToCheck--;

      if (validatorsToCheck === 0) {
        if (!hasValidationError) {
          onSuccess();
        }
      }
    };

    validatorParams.forEach(({ param, reportParamValues }) => {
      const paramNames = getParamNamesForQuery(param.query);
      const validationParams = pick(reportParamValues, paramNames);

      const hasAllRequiredParams = paramNames.every((paramName: string) =>
        paramName in validationParams && validationParams[paramName]
      );

      if (!hasAllRequiredParams) {
        validatorsToCheck--;
        if (validatorsToCheck === 0 && !hasValidationError) {
          onSuccess();
        }
        return;
      }

      verifyParamOptionsQuery({
        query: param.query,
        params: validationParams,
        onSuccess: onValidationSuccess,
        onFailure: onValidationFailure,
      });
    });
  }, [
    getValidatorParams,
    verifyParamOptionsQuery,
  ]);

  const handleValidationCancel = useCallback(() => {
    setShowValidationModal(false);
    setValidationMessage('');
    setPendingExecution(null);
  }, []);

  const handleValidationProceed = useCallback(() => {
    const callback = pendingExecution;
    setShowValidationModal(false);
    setValidationMessage('');
    setPendingExecution(null);

    if (callback) {
      callback();
    }
  }, [pendingExecution]);

  return {
    showValidationModal,
    validationMessage,
    runValidators,
    handleValidationCancel,
    handleValidationProceed,
  };
};

export default useBatchReportValidation;

